import { useMemo } from 'react'
import type { Contact, ContactDetail } from 'types/contacts'
import { cn } from 'utils/cn'
import { formatPhone } from 'utils/phone'

interface ContactField {
  key: string
  label: string
  format?: (v: string | null) => string | null | undefined
}

export const MERGE_FIELDS: ContactField[] = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phoneNumber', label: 'Phone', format: formatPhone },
  { key: 'organizationName', label: 'Organization' },
  { key: 'notes', label: 'Notes' },
  { key: 'ordoUserId', label: 'Ordo ID' },
  { key: 'timezone', label: 'Timezone' },
] as const

export type MergeFieldKey = (typeof MERGE_FIELDS)[number]['key']

export function contactDisplayName(contact: Contact): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || '(unnamed)'
}

export function formatFieldValue(field: (typeof MERGE_FIELDS)[number], value: string | null): string {
  if (!value) {
    return '-'
  }
  if (field.format) {
    return field.format(value) || value
  }
  return value
}

export interface MergeAssociation {
  id: string
  type: 'school' | 'vendor' | 'organization'
  externalId: string
  role: string
  contactId: string
  contactName: string
}

export function collectAssociations(contact: ContactDetail, name: string): MergeAssociation[] {
  const result: MergeAssociation[] = []
  for (const a of contact.schoolLocations) {
    result.push({
      id: a.id,
      type: 'school',
      externalId: a.ordoSchoolLocationId,
      role: a.role,
      contactId: contact.id,
      contactName: name,
    })
  }
  for (const a of contact.vendorLocations) {
    result.push({
      id: a.id,
      type: 'vendor',
      externalId: a.ordoVendorLocationId,
      role: a.role,
      contactId: contact.id,
      contactName: name,
    })
  }
  for (const a of contact.organizations) {
    result.push({
      id: a.id,
      type: 'organization',
      externalId: a.ordoOrganizationId,
      role: a.role,
      contactId: contact.id,
      contactName: name,
    })
  }
  return result
}

const ASSOCIATION_TYPE_LABELS: Record<MergeAssociation['type'], string> = {
  school: 'School Locations',
  vendor: 'Vendor Locations',
  organization: 'Organizations',
}

export function FieldSelector({
  field,
  contacts,
  selected,
  onSelect,
  namePrefix,
}: {
  field: (typeof MERGE_FIELDS)[number]
  contacts: (Contact | ContactDetail)[]
  selected: string
  onSelect: (contactId: string) => void
  namePrefix?: string
}) {
  const values = contacts.map((c) => ({
    id: c.id,
    value: c[field.key as keyof typeof c] as string | null,
  }))

  const distinctValues = new Map<string, string[]>()
  for (const { id, value } of values) {
    const display = formatFieldValue(field, value)
    const existing = distinctValues.get(display)
    if (existing) {
      existing.push(id)
    } else {
      distinctValues.set(display, [id])
    }
  }

  if (distinctValues.size <= 1) {
    const display = formatFieldValue(field, values[0]?.value ?? null)
    return (
      <div className="flex items-center gap-3 py-1.5">
        <span className="w-28 shrink-0 text-sm font-medium text-muted-foreground">{field.label}</span>
        <span className="text-sm">{display}</span>
      </div>
    )
  }

  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className="w-28 shrink-0 pt-0.5 text-sm font-medium text-muted-foreground">{field.label}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {values.map(({ id, value }) => {
          const display = formatFieldValue(field, value)
          return (
            <label key={id} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                name={`${namePrefix ?? 'merge'}-${field.key}`}
                checked={selected === id}
                onChange={() => onSelect(id)}
                className="accent-primary"
              />
              <span className={cn(!value && 'text-muted-foreground')}>{display}</span>
              <span className="text-xs text-muted-foreground">
                ({contactDisplayName(contacts.find((c) => c.id === id)!)})
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}

export function AssociationMergeSection({
  associations,
  excludedIds,
  onToggle,
}: {
  associations: MergeAssociation[]
  excludedIds: Set<string>
  onToggle: (id: string) => void
}) {
  const grouped = useMemo(() => {
    const groups: Record<MergeAssociation['type'], MergeAssociation[]> = {
      school: [],
      vendor: [],
      organization: [],
    }
    for (const a of associations) {
      groups[a.type].push(a)
    }
    return groups
  }, [associations])

  const types = (['school', 'vendor', 'organization'] as const).filter((t) => grouped[t].length > 0)

  if (types.length === 0) {
    return <div className="py-2 text-sm text-muted-foreground">No associations on any of the selected contacts.</div>
  }

  return (
    <div className="space-y-3">
      {types.map((type) => (
        <div key={type}>
          <div className="mb-1 text-sm font-medium text-muted-foreground">{ASSOCIATION_TYPE_LABELS[type]}</div>
          <div className="space-y-1">
            {grouped[type].map((assoc) => {
              const included = !excludedIds.has(assoc.id)
              return (
                <label key={assoc.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={included}
                    onChange={() => onToggle(assoc.id)}
                    className="accent-primary"
                  />
                  <span className={cn(!included && 'text-muted-foreground line-through')}>
                    {assoc.externalId}
                    {assoc.role && ` (${assoc.role})`}
                  </span>
                  <span className="text-xs text-muted-foreground">— {assoc.contactName}</span>
                </label>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

/** Build field overrides from field selections, returning only fields where user chose a non-target value */
export function buildFieldOverrides(
  fieldSelections: Record<MergeFieldKey, string>,
  targetId: string,
  contacts: (Contact | ContactDetail)[],
): Record<string, string | null> {
  const overrides: Record<string, string | null> = {}
  for (const field of MERGE_FIELDS) {
    const selectedId = fieldSelections[field.key]
    if (selectedId && selectedId !== targetId) {
      const source = contacts.find((c) => c.id === selectedId)
      if (source) {
        overrides[field.key] = source[field.key as keyof typeof source] as string | null
      }
    }
  }
  return overrides
}

/** Initialize field selections defaulting all fields to the given target ID */
export function initFieldSelectionsFor(targetId: string): Record<MergeFieldKey, string> {
  const selections: Record<string, string> = {}
  for (const field of MERGE_FIELDS) {
    selections[field.key] = targetId
  }
  return selections as Record<MergeFieldKey, string>
}
